import Vue from "vue";
import {pick} from "lodash";
import {RepositoryFactory} from "src/api/repositories/RepositoryFactory";
import {getUrlParams} from "../../../../commonjs/utils/UrlHandler";
import {selectPageInParentWindow} from "./pageUtils";
import deepCopy from "deepcopy";
import {successfulResponse} from "src/api/Response";

const PagesRepository = RepositoryFactory.get("pages");
const BlogPostRepository = RepositoryFactory.get("blogPosts");
const SiteTemplatesRepository = RepositoryFactory.get("siteTemplates");

const parentStore = window?.top?.parentVue?.$store;

function AsyncError(message) {
  const error = new Error(message);
  error.code = "async_error";
  return error;
}
AsyncError.prototype = Object.create(Error.prototype);

const defaultPageProperties = {
  title: "",
  slug: "",
  image: null,
  description: "",
  keywords: [],
  excludeFromSitemap: false,
  groups: [],
  type: "",
  has_top_bar: true,
  has_footer: true,
  has_single_footer_status: true,
  has_single_topbar_status: false,
  expiration: null,
  page_topbar_enabled: false,
  page_footer_enabled: false,
  access: "private",
};

export default {
    namespaced: true,
    state: {
        customStyles: [], //indexed array with the order that css changes took place during session, holds css changes that where created with the authoring tools
        currentPageParams: {},
        insertPageContentFlag: false, //flag used to initialise the insertion of the html content of the page
        pageData: {},
        dataToSave: {}, //holds all node information needed to save the page
        lastEditedCustomCss: "",
        pageCssLoggedIn: "",
        pageCssLoggedOut: "",
        pageHeadIncludesLoggedIn: "",
        pageHeadIncludesLoggedOut: "",
        pageBodyIncludesLoggedIn: "",
        pageBodyIncludesLoggedOut: "",
        nodesCustomStyling: "",
        lastEditedCustomJs: "",
        pageAdditionalData: {},
        homePageChanged: false, // Becomes true if the homepage is saved to now if we have to take another screenshot
        isAfterPurchasePage: $.getURLvar("after_purchase") === "true",
        isAfterLoginPage:
            typeof window.isAfterLogin != "undefined" ? window.isAfterLogin : false,
        clearPageFlag: false, //flag to trigger page empty contents
        emptyPageFlag: false, //flag to trigger page reset
        pageContext: {
            type: "page",
            params: {},
        },
        previewSiteTemplateId: window.previewSiteTemplateId? window.previewSiteTemplateId : false,
        isAfterLogin:  window.isAfterLogin? window.isAfterLogin : false,
        selectPageLoadQueue: [],
        currentPageLoad: null
    },
    getters: {
        isPost(state) {
            return state.pageData.type === "blog_post";
        },
        isCourse(state) {
            return state.pageData.type === "course";
        },
        isPageContext(state) {
            return function (pageType) {
                return state.pageContext && state.pageContext.type === pageType
                    ? true
                    : false;
            };
        },
        pageProperties(state) {
            if (!state.pageData) {
                return defaultPageProperties;
            }

            let pageProperties = pick(
                state.pageData,
                Object.keys(defaultPageProperties)
            );
            pageProperties.has_single_footer_status =
                pageProperties.has_single_footer_status === null
                    ? true
                    : pageProperties.has_single_footer_status; // todo remove if afterwards
            pageProperties.has_single_topbar_status =
                pageProperties.has_single_topbar_status === null
                    ? false
                    : pageProperties.has_single_topbar_status; // todo remove if afterwards

            return pageProperties;
        },
        pageContextFrom(state){            
            return state.pageContext?.params?.from;                    
        },
    },
    actions: {
        updatePageStyling({dispatch, state, rootState}) {
            let pageData = {...state.pageData};
            if (rootState.globalConfig.globalStylesPageContext) {
                pageData.style_config = deepCopy(rootState.globalConfig.globalStyles);
            } else {
                pageData.style_config = null;
            }
            dispatch("setPageData", pageData);
        },
        getPageData({commit}, {slug, type, site_template}) {
            if (type === "blog_post") {
                const params = {
                    "site_template" : true
                };
                return BlogPostRepository.getBlogPost(slug, params).then(({data}) => {
                    return data.blogPost;
                });
            }

            return PagesRepository.getPage(slug, type, site_template).then(
                ({data}) => {
                    return data.page;
                }
            );
        },
        savePage({ commit }, payload) {
          if (payload.type === "blog_post") {
            // remove seo for the blog post in order not to break the seo props
            const blogPostData = deepCopy(payload);
            delete blogPostData.seo;
            const params = {
              "site_template": true
            };
            return BlogPostRepository.saveBlogPost(
              blogPostData.slug,
              blogPostData,
              params
            ).then((axiosResult) => {
              if (successfulResponse(axiosResult)) {
                showMessage("success", "Success", "Properties saved");
                const page = axiosResult.data.blogPost;
                commit("setPageData", page);
                commit("blogCatalogue/updatePost", page, { root: true });
              } else {
                throw new AsyncError("Properties were not saved");
              }
            });
          }
          return PagesRepository.savePage(payload.slug, payload.type, payload.site_template, payload).then(
              (axiosResult) => {
                  if (successfulResponse(axiosResult)) {
                      showMessage("success", "Success", "Properties saved");

                      const page = axiosResult.data.page;
                      commit("setPageData", page);
                      commit("siteTemplate/updatePage", page, {root: true});
                      return page;
                  } else {
                      throw new AsyncError("Properties were not saved");
                  }
              }
          );
        },
        savePageProperties({dispatch, state}, payload) {
            const { slug, type, site_template } = state.pageData;
          
            const updatedPayload = deepCopy(payload);
            updatedPayload.slug = slug;
            updatedPayload.type = type;
            updatedPayload.site_template = site_template;
            return dispatch("savePage", updatedPayload).then( page => {
              const changePageUrl = state.pageData.slug !== page.slug;
              if (changePageUrl) {
                  selectPageInParentWindow({
                      type: page.type,
                      slug: page.slug,
                  });
              }
            }).catch( ({ code }) => {
              if (code === 'async_error'){
                  showMessage(
                      "error",
                      "An error occurred.",
                      "Properties were not saved"
                  );
              }
            })
        },
        saveSeo({dispatch, state}, {excludeFromSitemap, metadata}) {
            const {slug, type, site_template} = state.pageData;


            const metadataSave = deepCopy(metadata);

            if (metadataSave.image && metadataSave.image.name) {
                metadataSave.image = metadataSave.image.name;
            }

            if (type === "blog_post") {                
                const postSeo = {
                    seo: metadataSave ? {...metadataSave} : null,
                    excludeFromSitemap,
                };

                // SEO for blog post currently cannot be null to indicate that metadata
                // should be automatically extracted
                // TODO Change this in the future to null like pages
                if (postSeo.seo?.defaultSeo) {
                    postSeo.seo = {
                        title: "",
                        description: "",
                        keywords: [], // in blog post this is an array
                        image: "",
                    };
                }
                const params = {
                    "site_template" : true
                };
                return BlogPostRepository.saveBlogPostSeo(slug, postSeo, params).then(
                    (axiosResult) => {
                        if (successfulResponse(axiosResult)) {
                            showMessage("success", "Success", "Metadata was saved");
                            let pageData = {...state.pageData};

                            // We want to update the initial page data with the correct metadata and do not
                            // use the metadata object we sent as it does not have the correct structures
                            dispatch("setPageData", {
                                metadata: axiosResult.data.blogPost.metadata,
                                excludeFromSitemap,
                            });
                        } else {
                            // TODO Error ??
                            showMessage(
                                "error",
                                "An error occurred.",
                                "Metadata was not saved"
                            );
                            console.error("Failed to save page seo", axiosResult);
                        }
                    }
                );
            }

            const pageSeo = {
                metadata : metadataSave,
                excludeFromSitemap,
            };

            return PagesRepository.savePageSeo(
                slug,
                type,
                site_template,
                pageSeo
            ).then((axiosResult) => {
                if (successfulResponse(axiosResult)) {
                    showMessage("success", "Success", "Metadata was saved");
                    // TODO: Can we update the page data without re-setting the whole object?
                    // TODO: Why do we have two different properties? Can we update them both somehow?
                    let pageData = {...state.pageData};

                    // We want to update the initial page data with the correct metadata and do not
                    // use the metadata object we sent as it does not have the correct structures
                    dispatch("setPageData", {
                        metadata: axiosResult.data.metadata,
                        excludeFromSitemap,
                    });
                } else {
                    showMessage("error", "An error occurred.", "Metadata was not saved");
                    // TODO Error ??
                    console.error("Failed to save page seo", axiosResult);
                }
            });
        },
      initializePageData({ commit, dispatch }, payload) {

            if (!payload) {
                return;
            }

            if (payload && payload.style_config) {
                commit("globalConfig/setGlobalStylesPageContext", true, {root: true});
            } else {
                commit("globalConfig/setGlobalStylesPageContext", false, {
                    root: true,
                });
            }

            if (payload && payload.responsive_font_scale) {
                commit(
                    "siteTemplate/setResponsiveFontScale",
                    payload.responsive_font_scale,
                    {root: true}
                );
            }

            // dispatch('setPageContext',payload.type);

            dispatch("sections/setSectionsData", payload, {root: true});
            dispatch("elements/setElementsData", payload.elements, {root: true});
            // dispatch("setPageAdditionalData", payload.additionalData);
            dispatch("components/setComponentsData", payload.components, {
                root: true,
            });
            dispatch("globalConfig/updateGlobalStyles", null, {root: true});
            dispatch("topbar/setPageTopbarData", payload.topbar, {root: true});
            dispatch("footer/setPageFooterData", payload.footer, {root: true});
            dispatch("topbar/setSchoolTopbarEnabled", !payload.page_topbar_enabled, {
                root: true,
            });
            dispatch(
                "topbar/setHasSingleTopbarStatus",
                payload.has_single_topbar_status,
                {root: true}
            );
            dispatch("footer/setSchoolFooterEnabled", !payload.page_footer_enabled, {
                root: true,
            });
            dispatch(
                "footer/setHasSingleFooterStatus",
                payload.has_single_footer_status,
                {root: true}
            );

            commit("setPageData", payload);
        },
        setPageData({commit}, payload) {
            if (!payload) {
                return;
            }

            commit("setPageData", payload);
        },
        setPageContext({commit}, payload) {
            commit("setPageContext", payload);
        },
        buildNewPage({commit, dispatch}, payload) {
            let pageContent = document.querySelector("#pageContent");
            return dispatch("emptyPage")
                .then(() => {
                    return commit("topbar/setSchoolTopbarEnabled", null, {root: true});
                })
                .then(() => {
                    if (
                        payload.template.source.hasOwnProperty("api") &&
                        payload.template.source.api
                    ) {
                        return PagesRepository.buildPageAPI(payload);
                    } else {
                        return PagesRepository.buildPage(payload);
                    }
                })
                .then((response) => {
                    window.WI.track('Created Page');

                    if (response.data.success) {
                        commit(
                            "siteTemplate/addUpdateCurrentSiteTemplatePage",
                            response.data.page,
                            {root: true}
                        );

                        return;
                        // return dispatch("initializePageData", response.data.page);
                    }
                    throw response.data.errors;
                })
                // .then(() => {
                //     return dispatch("globalConfig/updateGlobalStyles", null, {
                //         root: true,
                //     });
                // })
                // .then(() => {
                //     return dispatch("siteTemplate/resetSchoolTopbarFooter", null, {
                //         root: true,
                //     });
                // })
                // .then(() => {
                //     return dispatch("setInsertPageContentFlag", true);
                // })
                // .then(() => {
                //     return dispatch("setPageContext", {
                //         type: payload.page.type,
                //         slug: payload.page.slug,
                //     });
                // })
                // .then(() => {
                //     return dispatch("sections/initSectionsManager", pageContent, {
                //         root: true,
                //     });
                // })
                // .then(() => {
                //     dispatch("elements/initElementsManager", pageContent, {root: true});
                //     dispatch("components/initComponentsManager", pageContent, {
                //         root: true,
                //     });
                // });
            // PagesRepository.buildPage(payload).then((result) => {

            //     return result;
            // })
        },
        selectPage({commit, dispatch, state, rootState}, payload) {
            
            
            if(state.currentPageLoad){
                commit('queueSelectPage', payload);
                return;
            }

            const queueLastSelectPage = () =>{
                
                commit('setCurrentPageLoad', false);
                if(state.selectPageLoadQueue.length){                    
                    dispatch('selectPage', state.selectPageLoadQueue[state.selectPageLoadQueue.length - 1]);
                    commit('resetQueueSelectPage', state.selectPageLoadQueue.length);
                } 
            }
            
            commit('setCurrentPageLoad', true);
            return dispatch('loadSelectPage', payload).then(()=>{
                queueLastSelectPage();
            }).catch(()=>{            
                queueLastSelectPage();
            });

        },
        loadSelectPage({commit, dispatch, state, rootState}, payload){    
            
            let {
                slug = payload.slug ? payload.slug : payload.page ? payload.page : "",
                type = "page",
                site_template = payload.site_template ||
                    rootState.siteTemplate.currentSiteTemplate,
            } = payload;
            
            let pageContent = document.querySelector("#pageContent");
            return new Promise( (resolve, reject) => {
                
                dispatch("emptyPage")
                .then(() => {
                    if (
                        payload.site_template &&
                        payload.site_template !== rootState.siteTemplate.currentSiteTemplate
                    ) {
                        dispatch(
                            "siteTemplate/setCurrentSiteTemplate",
                            payload.site_template,
                            {root: true}
                        );
                    }
                    return commit("topbar/setSchoolTopbarEnabled", null, {root: true});
                })
                .then(() => {                    
                    return dispatch("getPageData", {slug, type, site_template});
                })
                  .then((pageData) => {
                    return dispatch("initializePageData", pageData);
                })
                .then(() => {
                    return dispatch("globalConfig/updateGlobalStyles", null, {
                        root: true,
                    });
                })
                .then(() => {
                    return dispatch("siteTemplate/resetSchoolTopbarFooter", null, {
                        root: true,
                    });
                })
                .then(() => {
                  dispatch("setInsertPageContentFlag", true);
                  resolve();
                })
                .catch((err)=>{
                  reject();
                });               
            });
        },
        
        emptyPage({dispatch}) {
            //Remove the contents of the page and the topbar / footer
            dispatch("setEmptyPageFlag", true);
            dispatch("modalWindows/resetWindowsDisplay", null, {root: true});
            dispatch("editingSideform/setSideformPinned", false, {root: true}); //unpin sideform
            dispatch("editingSideform/setSideformDisplay", false, {root: true}); //also hide editing sideform
            dispatch("editingSideform/setSideformEditedNode", null, {root: true});

            Vue.nextTick(() => {
                dispatch("setEmptyPageFlag", false);
            });
        },
        clearPage({dispatch}) {
            dispatch("setClearPageFlag", true);
            dispatch("modalWindows/resetWindowsDisplay", null, {root: true});
            dispatch("editingSideform/setSideformPinned", false, {root: true}); //unpin sideform
            dispatch("editingSideform/setSideformDisplay", false, {root: true}); //also hide editing sideform
            dispatch("editingSideform/setSideformEditedNode", null, {root: true});
            dispatch("setChangesAreSaved", false, {root: true});
            Vue.nextTick(() => {
                dispatch("setClearPageFlag", false);
            });
        },
        deleteSpecificPage({state, rootState, dispatch}, {slug, siteTemplate}) {
            return PagesRepository.deletePage(slug, siteTemplate).then(() => {
                if (
                    siteTemplate === rootState.siteTemplate.currentSiteTemplate &&
                    state.pageContext.params &&
                    state.pageContext.params.slug === slug
                ) {
                    if (window.parent.parentVue) {
                        selectPageInParentWindow({
                            slug: "home",
                            type: "home",
                            site_template: state.currentSiteTemplate,
                        });
                    } else {
                        dispatch("selectPage", {
                            slug: "home",
                            type: "home",
                            site_template: state.currentSiteTemplate,
                        });
                    }

                    dispatch("setPageContext", {
                        slug: "home",
                        type: "home",
                    });
                }
            });
        },
        deletePage({commit, state, dispatch, rootState}) {
            const slug = state.pageData.slug ? state.pageData.slug : null;
            const site_template = rootState.siteTemplate.currentSiteTemplate
                ? rootState.siteTemplate.currentSiteTemplate
                : null;

            let request;
            if (state.pageContext.type === "blog_post") {
                const params = {
                    "site_template" : true
                };
                request = BlogPostRepository.deleteBlogPost(slug, params);
            } else {
                request = PagesRepository.deletePage(slug, site_template);
            }

            request
                .then()
                .then((response) => {
                    if (response.data.success) {
                        // Load home page
                        if (window.parent.parentVue) {
                            selectPageInParentWindow({
                                slug: "home",
                                type: "home",
                                site_template: site_template,
                            });
                        } else {
                            dispatch("selectPage", {
                                slug: "home",
                                type: "home",
                                site_template: site_template,
                            });
                        }

                        dispatch("setPageContext", {
                            slug: "home",
                            type: "home",
                        });
                        // Get pages again
                      dispatch("siteTemplate/retrieveSiteTemplatePages", { "site_template": site_template }, {
                            root: true,
                        });
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        removeUnsavedChanges({dispatch, state}){            
            dispatch("selectPage", state.pageData) 
        },
        resetPage({dispatch, state}) {
            let pageData = {...state.pageData};
            let pageContent = document.querySelector("#pageContent");
            //Todo Set the currentPageLoad to true while reseting the page in order to prevent topbar/footer wathcers from sections manager to trigger topbar/footer insertion
            dispatch("emptyPage")
                .then(() => {
                    return PagesRepository.resetPage(pageData.type, pageData.source);
                })
                .then((response) => {
                    pageData = {
                        ...pageData,
                        ...response.data.page,
                    };
                    return dispatch("initializePageData", pageData);
                })
                .then(() => {
                    return dispatch("globalConfig/updateGlobalStyles", null, {
                        root: true,
                    });
                })
                .then(() => {
                    return dispatch("siteTemplate/resetSchoolTopbarFooter", null, {
                        root: true,
                    });
                })
                .then(() => {
                    return dispatch("setInsertPageContentFlag", true);
                })
                .then(() => {
                    return dispatch("sections/initSectionsManager", pageContent, {
                        root: true,
                    });
                })
                .then(() => {
                    dispatch("elements/initElementsManager", pageContent, {root: true});
                    dispatch("components/initComponentsManager", pageContent, {
                        root: true,
                    });
                });
        },
        setClearPageFlag({commit}, payload) {
            commit("setClearPageFlag", payload);
        },
        setEmptyPageFlag({commit}, payload) {
            commit("setEmptyPageFlag", payload);
        },
        setInsertPageContentFlag({commit}, payload) {
            commit("setInsertPageContentFlag", payload);
        },
        setPageIncludes({commit}, payload) {
            commit("setPageIncludes", payload);
        },
        setNodesCustomStyling({commit}, payload) {
            commit("setNodesCustomStyling", payload);
        },
        prepareToSave({commit, dispatch, rootState, state, rootGetters, getters}, payload) {
            let clonedPageContent = document
                .querySelector("#pageContent")
                .cloneNode(true);
            let sectionStates = rootState.sections.sectionStates;
            let pageHasOwnStyleConfig =
                rootState.globalConfig.globalStylesPageContext;

            let dataToSave = {
                sections: {},
                components: {},
                elements: {},
                pageCustomCss: {},
                content: "",
                pageIncludes: {},
                additionalData: {},
                style_config: "",
                styles_compiled: "",
            };

            let topbarDataToSave = {
                "logged-in": {
                    components: {},
                    elements: {},
                    content: "",
                    styles_compiled: "",
                },
                "logged-out": {
                    components: {},
                    elements: {},
                    content: "",
                    styles_compiled: "",
                },
            };

            let footerDataToSave = {
                "logged-in": {
                    components: {},
                    elements: {},
                    content: "",
                    styles_compiled: "",
                },
                "logged-out": {
                    components: {},
                    elements: {},
                    items: {},
                    content: "",
                    styles_compiled: "",
                },
            };

            dispatch("checkElementContentsBeforeSave");

            //replacement of sanitised section segments
            for (let sectionId in sectionStates) {
                if (!sectionStates.hasOwnProperty(sectionId)) {
                    continue;
                }

                let sectionState = sectionStates[sectionId];

                let sectionDataToSave = sectionState.startSavingProcess();
                let sectionIdToSave = sectionDataToSave.sectionData.myData.sectionId;
                sectionState.removeMagicTemplateData(sectionIdToSave);

                let topbarSectionType = rootState.topbar.schoolTopbarEnabled
                    ? "school-topbar"
                    : "page-topbar";
                let footerSectionType = rootState.footer.schoolFooterEnabled
                    ? "schoolfooter"
                    : "pagefooter";

                if (
                    topbarSectionType ==
                    sectionStates[sectionId].sectionType.toLowerCase()
                ) {
                    let status = sectionStates[sectionId].topbarStatus;
                    topbarDataToSave[status] = sectionDataToSave.sectionData.myData;
                    topbarDataToSave[status].content =
                        sectionDataToSave.sectionData.myClone.outerHTML;
                    topbarDataToSave[status].components = {
                        ...topbarDataToSave[status].components,
                        ...sectionDataToSave.componentData,
                    };
                    topbarDataToSave[status].elements = {
                        ...topbarDataToSave[status].elements,
                        ...sectionDataToSave.elementData,
                    };
                    topbarDataToSave[status].content =
                        sectionDataToSave.sectionData.myClone.outerHTML;
                    topbarDataToSave[status].styles_compiled =
                        sectionDataToSave.sectionData.myData.styles_compiled;
                    clonedPageContent.querySelector("#" + sectionIdToSave).remove();
                    continue;
                }

                if (
                    footerSectionType ==
                    sectionStates[sectionId].sectionType.toLowerCase()
                ) {
                    let status = sectionStates[sectionId].footerStatus;
                    footerDataToSave[status] = sectionDataToSave.sectionData.myData;
                    footerDataToSave[status].content =
                        sectionDataToSave.sectionData.myClone.outerHTML;
                    footerDataToSave[status].components = {
                        ...footerDataToSave[status].components,
                        ...sectionDataToSave.componentData,
                    };
                    footerDataToSave[status].elements = {
                        ...footerDataToSave[status].elements,
                        ...sectionDataToSave.elementData,
                    };
                    footerDataToSave[status].content =
                        sectionDataToSave.sectionData.myClone.outerHTML;
                    footerDataToSave[status].styles_compiled =
                        sectionDataToSave.sectionData.myData.styles_compiled;
                    clonedPageContent.querySelector("#" + sectionIdToSave).remove();
                    continue;
                }

                clonedPageContent.replaceChild(
                    sectionDataToSave.sectionData.myClone,
                    clonedPageContent.querySelector("#" + sectionIdToSave)
                );

                dataToSave.sections[sectionIdToSave] =
                    sectionDataToSave.sectionData.myData;
                dataToSave.components = {
                    ...dataToSave.components,
                    ...sectionDataToSave.componentData,
                };
                dataToSave.elements = {
                    ...dataToSave.elements,
                    ...sectionDataToSave.elementData,
                };
            }

            dataToSave.content = clonedPageContent.innerHTML;
            dataToSave.additionalData = state.pageAdditionalData;

            if (state.pageCssLoggedIn) {
                dataToSave.pageCustomCss["logged-in"] = state.pageCssLoggedIn;
            }

            if (state.pageCssLoggedOut) {
                dataToSave.pageCustomCss["logged-out"] = state.pageCssLoggedOut;
            }

            if (state.pageHeadIncludesLoggedIn) {
                if (!dataToSave.pageIncludes["logged-in"]) {
                    dataToSave.pageIncludes["logged-in"] = {};
                }

                dataToSave.pageIncludes["logged-in"].head =
                    state.pageHeadIncludesLoggedIn;
            }

            if (state.pageHeadIncludesLoggedOut) {
                if (!dataToSave.pageIncludes["logged-out"]) {
                    dataToSave.pageIncludes["logged-out"] = {};
                }

                dataToSave.pageIncludes["logged-out"].head =
                    state.pageHeadIncludesLoggedOut;
            }

            if (state.pageBodyIncludesLoggedIn) {
                if (!dataToSave.pageIncludes["logged-in"]) {
                    dataToSave.pageIncludes["logged-in"] = {};
                }

                dataToSave.pageIncludes["logged-in"].body =
                    state.pageBodyIncludesLoggedIn;
            }

            if (state.pageBodyIncludesLoggedOut) {
                if (!dataToSave.pageIncludes["logged-out"]) {
                    dataToSave.pageIncludes["logged-out"] = {};
                }

                dataToSave.pageIncludes["logged-out"].body =
                    state.pageBodyIncludesLoggedOut;
            }

            dataToSave = {
                ...dataToSave,
                ...getters.pageProperties,
                page_topbar_enabled: !rootState.topbar.schoolTopbarEnabled,
                page_footer_enabled: !rootState.footer.schoolFooterEnabled,
                has_single_footer_status: rootState.footer.hasSingleFooterStatus,
                has_single_topbar_status: rootState.topbar.hasSingleTopbarStatus,
            };

            if (!rootState.topbar.schoolTopbarEnabled && state.pageData.has_top_bar) {
                dataToSave["topbar"] = topbarDataToSave;
            }

            if (!rootState.footer.schoolFooterEnabled && state.pageData.has_footer) {
                dataToSave["footer"] = footerDataToSave;
            }

            if (pageHasOwnStyleConfig) {
                dataToSave.style_config = rootState.globalConfig.globalStyles;
                dataToSave.styles_compiled =
                    rootState.globalConfig.globalGeneratedStyles +
                    state.nodesCustomStyling;
            } else {
                dataToSave.styles_compiled = state.nodesCustomStyling;
            }

            if (dataToSave.slug === "home") {
                state.homePageChanged = true;
            }

            let templateData = {
                ...rootGetters["siteTemplate/currentSiteTemplateData"],
            };

            if (!pageHasOwnStyleConfig) {
                templateData = {
                    ...templateData,
                    ...{styling: rootState.globalConfig.globalStyles},
                    ...{styles_compiled: rootState.globalConfig.globalGeneratedStyles},
                };
            }

            if (rootState.topbar.schoolTopbarEnabled && state.pageData.has_top_bar) {
                templateData = {
                    ...templateData,
                    ...{topbar: topbarDataToSave},
                };
            }

            if (rootState.footer.schoolFooterEnabled && state.pageData.has_footer) {
                templateData = {
                    ...templateData,
                    ...{footer: footerDataToSave},
                };
            }

            if (rootState.siteTemplate.responsiveFontScale) {
                templateData = {
                    ...templateData,
                    ...{
                        responsive_font_scale: rootState.siteTemplate.responsiveFontScale,
                    },
                };
            }

            let saveCurrentPage = null;
            if (state.pageContext.type === "blog_post") {
                const params = {
                    site_template: true
                };
                saveCurrentPage = BlogPostRepository.saveBlogPost(
                    state.pageData.slug,
                    dataToSave,
                    params
                );
            } else {
                saveCurrentPage = PagesRepository.savePage(
                    state.pageData.slug,
                    state.pageData.type,
                    state.pageData.site_template,
                    dataToSave
                );
            }

            // instructors can only save page
            if (window.me.isAInstructor && state.pageData.type === 'course') {
                return Promise.all([saveCurrentPage]);
            }

            const saveCurrentTemplate = SiteTemplatesRepository.saveTemplate(
                templateData
            );

            saveCurrentTemplate.then(({data}) => {
                commit('setChangesAreSaved', true, {root:true});
                if (data.templates) {
                    // commit("siteTemplate/setSiteTemplates", data.templates, {
                    //     root: true,
                    // });

                    if (parentStore) {
                        parentStore.commit("SET_SITETEMPLATES", data.templates);
                    }
                }

                if (payload === "withRedirect") {
                    window.parent.postMessage({action: "continueToRedirect"}, "*");
                    broadcastEvent({action: "saveFinished"}, true);
                }
            });

            if (typeof window.WI !== "undefined") {
                window.WI.track('Save Page');
            }

            return Promise.all([saveCurrentPage, saveCurrentTemplate]);
        },
        setPageAdditionalData({commit}, payload) {
            commit("setPageAdditionalData", payload);
            commit("setPageAdditionalData", payload);
        },
        checkElementContentsBeforeSave({rootState, getters}){
            //Check if the payment Page includes  checkout functionality            
            if(getters.isPageContext('payment')){                
                const hasCheckout = rootState.elements.registeredElements.find( element => element.elementType == 'payment-manager') ? true : false;
                if(!hasCheckout){
                    showMessage('warning', "" ,"The school payment page will not be functional since it doesn't includes a payments widget. Ensure that this is what you want!");
                }
            }
        }
    },
    mutations: {
        setNodesCustomStyling(state, data) {
            state.nodesCustomStyling = data;
        },
        setClearPageFlag(state, flag) {
            state.clearPageFlag = flag;
        },
        setEmptyPageFlag(state, flag) {
            state.emptyPageFlag = flag;
        },
        setPageContext(state, args) {
            let pageContext = {type: "", params: {}};

            Object.keys(args).forEach((param) => {
                const paramValue = args[param];

                if (param === "type") {
                    pageContext[param] = paramValue;
                } else if (param === "page") {
                    pageContext.params["slug"] = paramValue;
                } else {
                    pageContext.params[param] = paramValue;
                }
            });

            if (pageContext.params.slug === "home") {
                pageContext.type = "home";
            }

            state.pageContext = pageContext;
        },
      setPageData(state, payload) {
            state.pageData = {...state.pageData, ...payload};
            state.pageCssLoggedIn =
                (state.pageData.pageCustomCss &&
                    state.pageData.pageCustomCss["logged-in"]) ||
                "";
            state.pageCssLoggedOut =
                (state.pageData.pageCustomCss &&
                    state.pageData.pageCustomCss["logged-out"]) ||
                "";
            state.pageHeadIncludesLoggedIn =
                (state.pageData.pageIncludes &&
                    state.pageData.pageIncludes["logged-in"] &&
                    state.pageData.pageIncludes["logged-in"].head) ||
                "";
            state.pageHeadIncludesLoggedOut =
                (state.pageData.pageIncludes &&
                    state.pageData.pageIncludes["logged-out"] &&
                    state.pageData.pageIncludes["logged-out"].head) ||
                "";
            state.pageBodyIncludesLoggedIn =
                (state.pageData.pageIncludes &&
                    state.pageData.pageIncludes["logged-in"] &&
                    state.pageData.pageIncludes["logged-in"].body) ||
                "";
            state.pageBodyIncludesLoggedOut =
                (state.pageData.pageIncludes &&
                    state.pageData.pageIncludes["logged-out"] &&
                    state.pageData.pageIncludes["logged-out"].body) ||
                "";
            state.pageDataLoaded = true;
            state.pageAdditionalData = state.pageData.additionalData
                ? state.pageData.additionalData
                : {};

            //state.globalStyles = payload.style_config || {...state.globalStyles}
            //state.globalStyles = merge(state.globalStyles, payload.style_config)
            //state.globalGeneratedStyles = payload.styles_compiled || '';

            if (getUrlParams().type && getUrlParams().type === "course") {
                state.pageBelongsToCourse = true;
                state.pageCourseId = getUrlParams().page;
            }
        },
        setPageAdditionalData(state, data) {
            Object.keys(data).forEach((key) => {
                let keyValue = data[key];
                Vue.set(state.pageAdditionalData, key, keyValue);
            });
        },
        setInsertPageContentFlag(state, flag) {
            state.insertPageContentFlag = flag;
        },
        setPageIncludes(state, data) {
            if (typeof state[data.key] !== "undefined") {
                Vue.set(state, data.key, data.content);
            }
        },
        queueSelectPage(state, payload){
            state.selectPageLoadQueue.push(payload);
        },
        resetQueueSelectPage(state, length){
            state.selectPageLoadQueue= [];
            
        },
        setCurrentPageLoad(state, payload){
            state.currentPageLoad = payload;
        }
    },
};
